/**
 * Utilities for preserving structural HTML document tags (<html>, <head>, <style>, <body>)
 * that Tiptap would otherwise strip. The approach: extract the document envelope before
 * passing content to Tiptap, store it, and re-wrap the editor output on every change.
 */

export interface DocumentParts {
  /** Everything before the body content: doctype, <html>, <head> (including <style>), <body> open tag */
  beforeBody: string;
  /** The inner content of <body>, or the entire input if not a full document */
  bodyContent: string;
  /** Closing </body> and </html> tags */
  afterBody: string;
  /** Extracted CSS strings from <style> tags in <head> (for visual injection into editor) */
  styles: string[];
  /** Whether the input was detected as a full HTML document */
  isFullDocument: boolean;
}

/**
 * Extracts the structural envelope from a full HTML document, separating it
 * from the editable body content. Uses regex (not DOMParser) to preserve
 * the exact input format without normalization.
 */
export function extractDocumentParts(html: string): DocumentParts {
  const empty: DocumentParts = {
    beforeBody: '',
    bodyContent: html,
    afterBody: '',
    styles: [],
    isFullDocument: false,
  };

  if (!html) return empty;

  // Detect full document by presence of <body> or <html> tags
  const hasBody = /<body[\s>]/i.test(html);
  const hasHtml = /<html[\s>]/i.test(html);

  if (!hasBody && !hasHtml) return empty;

  let beforeBody = '';
  let bodyContent = html;
  let afterBody = '';

  if (hasBody) {
    // Find the <body...> open tag
    const bodyOpenMatch = html.match(/<body[^>]*>/i);
    if (bodyOpenMatch) {
      const bodyOpenIndex = html.indexOf(bodyOpenMatch[0]);
      const bodyOpenEnd = bodyOpenIndex + bodyOpenMatch[0].length;

      // Find closing </body> tag
      const bodyCloseMatch = html.match(/<\/body\s*>/i);
      const bodyCloseIndex = bodyCloseMatch
        ? html.indexOf(bodyCloseMatch[0])
        : html.length;

      beforeBody = html.substring(0, bodyOpenEnd);
      bodyContent = html.substring(bodyOpenEnd, bodyCloseIndex);
      afterBody = html.substring(bodyCloseIndex);
    }
  } else if (hasHtml) {
    // Has <html> but no <body> — treat content inside <html> as body
    const htmlOpenMatch = html.match(/<html[^>]*>/i);
    const htmlCloseMatch = html.match(/<\/html\s*>/i);
    if (htmlOpenMatch) {
      const htmlOpenEnd = html.indexOf(htmlOpenMatch[0]) + htmlOpenMatch[0].length;
      const htmlCloseIndex = htmlCloseMatch
        ? html.indexOf(htmlCloseMatch[0])
        : html.length;

      beforeBody = html.substring(0, htmlOpenEnd);
      bodyContent = html.substring(htmlOpenEnd, htmlCloseIndex);
      afterBody = html.substring(htmlCloseIndex);
    }
  }

  // Extract <style> contents from beforeBody (head section only)
  const styles: string[] = [];
  const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
  let styleMatch;
  while ((styleMatch = styleRegex.exec(beforeBody)) !== null) {
    styles.push(styleMatch[1]);
  }

  return {
    beforeBody,
    bodyContent,
    afterBody,
    styles,
    isFullDocument: true,
  };
}

/**
 * Reassembles a full HTML document by splicing body content back into
 * the stored structural envelope.
 */
export function reassembleDocument(bodyContent: string, parts: DocumentParts): string {
  if (!parts.isFullDocument) {
    return bodyContent;
  }
  return parts.beforeBody + bodyContent + parts.afterBody;
}

/**
 * Scopes CSS selectors to a container selector so styles only apply
 * within the editor, preventing leakage to the host page.
 *
 * - `body`, `html`, `:root` selectors → replaced with containerSelector
 * - Other selectors → prefixed with `containerSelector `
 * - `@media`/`@supports` blocks → inner rules are scoped recursively
 * - `@keyframes`, `@font-face`, `@import` → passed through unchanged
 */
export function scopeStylesToContainer(css: string, containerSelector: string): string {
  return processBlock(css, containerSelector);
}

function processBlock(css: string, containerSelector: string): string {
  const result: string[] = [];
  let i = 0;
  const len = css.length;

  while (i < len) {
    // Skip whitespace
    const wsStart = i;
    while (i < len && /\s/.test(css[i])) i++;
    if (i > wsStart) result.push(css.substring(wsStart, i));
    if (i >= len) break;

    // Check for at-rules
    if (css[i] === '@') {
      const atRuleStart = i;
      // Read the at-rule name
      let j = i + 1;
      while (j < len && /[a-zA-Z-]/.test(css[j])) j++;
      const atName = css.substring(i + 1, j).toLowerCase();

      if (atName === 'media' || atName === 'supports') {
        // Find the opening brace
        while (j < len && css[j] !== '{') j++;
        const prelude = css.substring(atRuleStart, j + 1);
        result.push(prelude);
        i = j + 1;

        // Find matching closing brace
        const innerStart = i;
        let depth = 1;
        while (i < len && depth > 0) {
          if (css[i] === '{') depth++;
          else if (css[i] === '}') depth--;
          if (depth > 0) i++;
        }
        const innerCss = css.substring(innerStart, i);
        result.push(processBlock(innerCss, containerSelector));
        if (i < len) {
          result.push('}');
          i++; // skip closing brace
        }
      } else {
        // @keyframes, @font-face, @import, @charset, etc. — pass through as-is
        if (css.indexOf('{', j) !== -1 && (atName === 'keyframes' || atName === 'font-face' || atName === 'layer')) {
          // Has a block — find matching closing brace
          while (j < len && css[j] !== '{') j++;
          j++; // skip opening brace
          let depth = 1;
          while (j < len && depth > 0) {
            if (css[j] === '{') depth++;
            else if (css[j] === '}') depth--;
            j++;
          }
          result.push(css.substring(atRuleStart, j));
          i = j;
        } else {
          // No block (e.g., @import, @charset) — read until semicolon
          while (j < len && css[j] !== ';') j++;
          if (j < len) j++; // include semicolon
          result.push(css.substring(atRuleStart, j));
          i = j;
        }
      }
    } else {
      // Regular rule-set: read selector until '{'
      const selectorStart = i;
      while (i < len && css[i] !== '{') i++;
      if (i >= len) {
        // No opening brace found — trailing text, output as-is
        result.push(css.substring(selectorStart));
        break;
      }

      const selectorText = css.substring(selectorStart, i);
      const scopedSelector = scopeSelectorList(selectorText.trim(), containerSelector);
      result.push(scopedSelector + ' {');
      i++; // skip opening brace

      // Find matching closing brace (declarations)
      const declStart = i;
      let depth = 1;
      while (i < len && depth > 0) {
        if (css[i] === '{') depth++;
        else if (css[i] === '}') depth--;
        if (depth > 0) i++;
      }
      result.push(css.substring(declStart, i));
      if (i < len) {
        result.push('}');
        i++; // skip closing brace
      }
    }
  }

  return result.join('');
}

function scopeSelectorList(selectorList: string, containerSelector: string): string {
  return selectorList
    .split(',')
    .map(sel => scopeSelector(sel.trim(), containerSelector))
    .join(', ');
}

function scopeSelector(selector: string, containerSelector: string): string {
  if (!selector) return selector;

  // body or html selector → replace with container
  if (/^(html|body)\b/i.test(selector)) {
    return selector.replace(/^(html|body)/i, containerSelector);
  }

  // :root selector → replace with container
  if (selector.startsWith(':root')) {
    return selector.replace(':root', containerSelector);
  }

  // * universal selector alone → container *
  if (selector === '*') {
    return `${containerSelector} *`;
  }

  // Everything else: prefix with container
  return `${containerSelector} ${selector}`;
}
